import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CrownIcon, CheckCircleIcon, ArrowRightIcon, ChevronRightIcon, ShieldCheckIcon, ClockIcon, UsersIcon, StarIcon, BriefcaseIcon, EyeOffIcon, TrendingUpIcon, ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { ContactForm } from '../../components/ContactForm';
import { useSEO } from '../../hooks/useSEO';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslations } from '../../i18n/translations';
function HeroSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  return <section className="relative pt-20 lg:pt-24 pb-20 overflow-hidden bg-navy-900" data-id="element-942">
      <div className="absolute inset-0" data-id="element-943">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-primary-500/8 blur-3xl hidden sm:block" data-id="element-944" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-cyan-500/6 blur-3xl hidden sm:block" data-id="element-945" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} data-id="element-946" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-id="element-947">
        <motion.nav initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="flex items-center gap-1.5 text-sm text-navy-400 mb-10" data-id="element-948">
          <Link to="/" className="hover:text-primary-400 transition-colors" data-id="element-949">
            {t.nav.home}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-950" />
          <Link to="/#services" className="hover:text-primary-400 transition-colors" data-id="element-951">
            {t.nav.services}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-952" />
          <span className="text-navy-300" data-id="element-953">{t.executiveSearch.title}</span>
        </motion.nav>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-954">
          <div data-id="element-955">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.05
          }} className="flex items-center gap-3 mb-6" data-id="element-956">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 text-white shadow-xl shadow-primary-500/30" data-id="element-957">
                <CrownIcon className="w-6 h-6" data-id="element-958" />
              </div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-500/20 text-primary-300 text-xs font-semibold rounded-full border border-primary-500/20" data-id="element-959">
                {t.executiveSearch.badge}
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.1
          }} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.08]" data-id="element-960">
              {t.executiveSearch.h1_1}{' '}
              <span className="text-primary-400" data-id="element-961">
                {t.executiveSearch.h1_accent}
              </span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.18
          }} className="mt-6 text-xl text-navy-300 leading-relaxed" data-id="element-962">
              {t.executiveSearch.subtitle}
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.25
          }} className="mt-8 flex flex-wrap gap-4" data-id="element-963">
              <a href="#contact" className="inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-navy-900 bg-white rounded-xl hover:bg-navy-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-964">
                {t.executiveSearch.cta_primary}
                <ArrowRightIcon className="w-4 h-4" data-id="element-965" />
              </a>
              <a href="#process" className="inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-white border border-white/20 rounded-xl hover:bg-white/10 transition-all" data-id="element-966">
                {t.executiveSearch.cta_secondary}
              </a>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="grid grid-cols-2 gap-4" data-id="element-967">
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-968">
              <div className="text-4xl font-extrabold text-white" data-id="element-969">
                {t.executiveSearch.stats.days_val}
              </div>
              <div className="text-sm text-navy-400 mt-1" data-id="element-970">
                {t.executiveSearch.stats.days_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-971">
              <div className="text-4xl font-extrabold text-primary-400" data-id="element-972">
                {t.executiveSearch.stats.guarantee_val}
              </div>
              <div className="text-sm text-navy-400 mt-1" data-id="element-973">
                {t.executiveSearch.stats.guarantee_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-974">
              <div className="text-4xl font-extrabold text-white" data-id="element-975">
                {t.executiveSearch.stats.closed_val}
              </div>
              <div className="text-sm text-navy-400 mt-1" data-id="element-976">
                {t.executiveSearch.stats.closed_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-977">
              <div className="text-4xl font-extrabold text-primary-400" data-id="element-978">
                {t.executiveSearch.stats.success_val}
              </div>
              <div className="text-sm text-navy-400 mt-1" data-id="element-979">
                {t.executiveSearch.stats.success_label}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function WhatWeDoSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-white" ref={ref} data-id="element-980">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-981">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-982">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-983">
            {t.executiveSearch.whatWeDo.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-984">
            {t.executiveSearch.whatWeDo.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-985">
            {t.executiveSearch.whatWeDo.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-986">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="p-7 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-987">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-988">
              <EyeOffIcon className="w-5 h-5" data-id="element-989" />
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-990">
              {t.executiveSearch.whatWeDo.items[0].title}
            </h3>
            <p className="text-navy-500 text-sm leading-relaxed" data-id="element-991">
              {t.executiveSearch.whatWeDo.items[0].desc}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="p-7 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-992">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-993">
              <UsersIcon className="w-5 h-5" data-id="element-994" />
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-995">
              {t.executiveSearch.whatWeDo.items[1].title}
            </h3>
            <p className="text-navy-500 text-sm leading-relaxed" data-id="element-996">
              {t.executiveSearch.whatWeDo.items[1].desc}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="p-7 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-997">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-998">
              <ShieldCheckIcon className="w-5 h-5" data-id="element-999" />
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1000">
              {t.executiveSearch.whatWeDo.items[2].title}
              <span className="ml-2 text-xs font-semibold text-primary-600 bg-primary-50 border border-primary-100 px-2 py-0.5 rounded-full" data-id="element-1001">
                {t.executiveSearch.whatWeDo.items[2].badge}
              </span>
            </h3>
            <p className="text-navy-500 text-sm leading-relaxed" data-id="element-1002">
              {t.executiveSearch.whatWeDo.items[2].desc}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.25
        }} className="p-7 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1003">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1004">
              <StarIcon className="w-5 h-5" data-id="element-1005" />
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1006">
              {t.executiveSearch.whatWeDo.items[3].title}
            </h3>
            <p className="text-navy-500 text-sm leading-relaxed" data-id="element-1007">
              {t.executiveSearch.whatWeDo.items[3].desc}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="p-7 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1008">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1009">
              <BriefcaseIcon className="w-5 h-5" data-id="element-1010" />
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1011">
              {t.executiveSearch.whatWeDo.items[4].title}
            </h3>
            <p className="text-navy-500 text-sm leading-relaxed" data-id="element-1012">
              {t.executiveSearch.whatWeDo.items[4].desc}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.35
        }} className="p-7 rounded-2xl bg-gradient-to-br from-primary-600 to-primary-700 border border-primary-500" data-id="element-1013">
            <div className="w-12 h-12 rounded-xl bg-white/20 text-white flex items-center justify-center mb-5" data-id="element-1014">
              <TrendingUpIcon className="w-5 h-5" data-id="element-1015" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2" data-id="element-1016">
              {t.executiveSearch.whatWeDo.items[5].title}
            </h3>
            <p className="text-primary-100 text-sm leading-relaxed" data-id="element-1017">
              {t.executiveSearch.whatWeDo.items[5].desc}
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}
function PositionsSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const positions = ['CEO / Генеральний директор', 'COO / Операційний директор', 'CFO / Фінансовий директор', 'CTO / Технічний директор', 'CMO / Директор з маркетингу', 'CHRO / HR директор', 'CSO / Директор з продажів', 'CDO / Директор з цифрової трансформації', 'Board Member / Член ради директорів', 'Country Manager', 'VP of Engineering', 'VP of Product'];
  return <section className="py-20 lg:py-28 bg-navy-50/40" ref={ref} data-id="element-1018">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1019">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start" data-id="element-1020">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-1021">
            <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1022">
              {t.executiveSearch.positions.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-navy-900 tracking-tight" data-id="element-1023">
              {t.executiveSearch.positions.title}
            </h2>
            <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-1024">
              {t.executiveSearch.positions.subtitle}
            </p>
            <div className="mt-8 p-6 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1025">
              <div className="flex items-start gap-3" data-id="element-1026">
                <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-1027" />
                <p className="text-navy-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{
                __html: t.executiveSearch.positions.note.replace('15+ років', '<strong class="text-navy-900">15+ років</strong>').replace('15+ лет', '<strong class="text-navy-900">15+ лет</strong>').replace('15+ years', '<strong class="text-navy-900">15+ years</strong>')
              }} data-id="element-1028" />
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1029">
            {t.executiveSearch.positions.items.map((pos, i) => <motion.div key={pos} initial={{
            opacity: 0,
            y: 12
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.4,
            delay: 0.1 + i * 0.04
          }} className="flex items-center gap-2.5 p-3.5 rounded-xl bg-white border border-navy-100" data-id="element-1030">
                <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" data-id="element-1031" />
                <span className="text-sm font-medium text-navy-700" data-id="element-1032">{pos}</span>
              </motion.div>)}
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.4,
            delay: 0.1 + t.executiveSearch.positions.items.length * 0.04
          }} className="sm:col-span-2 flex items-center gap-2.5 p-3.5 rounded-xl bg-primary-50 border border-primary-100" data-id="element-1033">
              <div className="w-2 h-2 rounded-full bg-primary-400 flex-shrink-0" data-id="element-1034" />
              <span className="text-sm font-medium text-primary-700 italic" data-id="element-1035">
                {t.executiveSearch.positions.other}
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function ProcessSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const steps = t.executiveSearch.process.steps.map((step, i) => ({
    num: `0${i + 1}`,
    title: step.title,
    desc: step.desc,
    side: i % 2 === 0 ? 'left' : 'right'
  }));
  return <section className="py-20 lg:py-28 bg-white" id="process" ref={ref} data-id="element-1036">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1037">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-16" data-id="element-1038">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1039">
            {t.executiveSearch.process.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-1040">
            {t.executiveSearch.process.title}
          </h2>
        </motion.div>

        {/* Desktop timeline */}
        <div className="hidden md:block relative" data-id="element-1041">
          {/* Center line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary-200 via-primary-300 to-primary-100 -translate-x-1/2" data-id="element-1042" />

          <div className="space-y-12" data-id="element-1043">
            {steps.map((step, i) => <motion.div key={step.num} initial={{
            opacity: 0,
            y: 24
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className={`relative flex items-center gap-8 ${step.side === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-1044">
                {/* Content card */}
                <div className="w-[calc(50%-2.5rem)]" data-id="element-1045">
                  <div className={`p-6 rounded-2xl bg-navy-50/70 border border-navy-100 ${step.side === 'right' ? 'text-right' : ''}`} data-id="element-1046">
                    <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1047">
                      {step.title}
                    </h3>
                    <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1048">
                      {step.desc}
                    </p>
                  </div>
                </div>

                {/* Center node */}
                <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center w-14 h-14 rounded-full bg-white border-2 border-primary-300 shadow-lg shadow-primary-100/50 z-10" data-id="element-1049">
                  <span className="text-sm font-extrabold text-primary-600" data-id="element-1050">
                    {step.num}
                  </span>
                </div>

                {/* Empty side */}
                <div className="w-[calc(50%-2.5rem)]" data-id="element-1051" />
              </motion.div>)}
          </div>
        </div>

        {/* Mobile timeline */}
        <div className="md:hidden relative pl-10" data-id="element-1052">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-gradient-to-b from-primary-200 via-primary-300 to-primary-100" data-id="element-1053" />
          <div className="space-y-8" data-id="element-1054">
            {steps.map((step, i) => <motion.div key={step.num} initial={{
            opacity: 0,
            x: -16
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.4,
            delay: i * 0.08
          }} className="relative" data-id="element-1055">
                <div className="absolute -left-10 top-4 flex items-center justify-center w-8 h-8 rounded-full bg-white border-2 border-primary-300 shadow-md z-10" data-id="element-1056">
                  <span className="text-[10px] font-extrabold text-primary-600" data-id="element-1057">
                    {step.num}
                  </span>
                </div>
                <div className="p-5 rounded-2xl bg-navy-50/70 border border-navy-100" data-id="element-1058">
                  <h3 className="text-base font-bold text-navy-900 mb-1.5" data-id="element-1059">
                    {step.title}
                  </h3>
                  <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1060">
                    {step.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}
function CtaBanner() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5
  }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1061">
      <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 relative overflow-hidden" data-id="element-1062">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-primary-500/10 blur-3xl hidden sm:block" data-id="element-1063" />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-primary-400/10 blur-3xl hidden sm:block" data-id="element-1064" />
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10" data-id="element-1065">
          <div className="flex-1" data-id="element-1066">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-1067">
              {t.executiveSearch.ctaBanner.title}
            </h3>
            <p className="mt-2 text-navy-300 leading-relaxed" data-id="element-1068">
              {t.executiveSearch.ctaBanner.subtitle}
            </p>
          </div>
          <a href="#contact" className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-navy-900 bg-white rounded-xl hover:bg-navy-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-1069">
            {t.executiveSearch.ctaBanner.button}
            <ArrowRightIcon className="w-4 h-4" data-id="element-1070" />
          </a>
        </div>
      </div>
    </motion.div>;
}
const seoData = {
  UA: {
    title: 'Executive Search — Підбір керівників вищої ланки | ESCA',
    description: 'Професійний підбір топ-менеджерів та керівників вищої ланки. CEO, CFO, CTO, COO — знаходимо лідерів для вашого бізнесу. 20+ років досвіду.',
    keywords: 'executive search, підбір керівників, хедхантинг, топ-менеджмент, CEO, CFO, CTO, ESCA'
  },
  RU: {
    title: 'Executive Search — Подбор руководителей высшего звена | ESCA',
    description: 'Профессиональный подбор топ-менеджеров и руководителей высшего звена. CEO, CFO, CTO, COO — находим лидеров для вашего бизнеса. 20+ лет опыта.',
    keywords: 'executive search, подбор руководителей, хедхантинг, топ-менеджмент, CEO, CFO, CTO, ESCA'
  },
  EN: {
    title: 'Executive Search — C-Level & Senior Leadership Recruitment | ESCA',
    description: 'Professional executive search for C-level and senior leadership positions. CEO, CFO, CTO, COO — we find leaders for your business. 20+ years of experience.',
    keywords: 'executive search, C-level recruitment, headhunting, senior leadership, CEO, CFO, CTO, ESCA'
  }
};
export function ExecutiveSearchPage() {
  const {
    lang
  } = useLanguage();
  const seo = seoData[lang];
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords
  });
  return <div className="min-h-screen w-full bg-white overflow-x-hidden" data-id="element-1071">
      <Header data-id="element-1072" />
      <main className="pt-16 lg:pt-20" data-id="element-1073">
        <HeroSection data-id="element-1074" />
        <WhatWeDoSection data-id="element-1075" />
        <PositionsSection data-id="element-1076" />
        <ProcessSection data-id="element-1077" />
        <CtaBanner data-id="element-1078" />
        <div id="contact" data-id="element-1079">
          <ContactForm data-id="element-1080" />
        </div>
      </main>
      <Footer data-id="element-1081" />
    </div>;
}